import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  Globe2, Cog, Waves, ShieldCheck, Truck, Boxes, Wrench, Landmark,
  ArrowRight, ArrowUpRight, Menu, X, MapPin, Award, CheckCircle2,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { SiteNav, SiteFooter } from "@/components/SiteChrome";
import WorldMap from "@/components/WorldMap";
import QuoteSection from "@/components/RfqSection";
import { ProductsCapabilities, ManufacturingQuality } from "@/components/DeepSections";

const ease = [0.22, 1, 0.36, 1];
const NAVY = "#0A192F";

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div className={className}
      initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.7, ease, delay }}>
      {children}
    </motion.div>
  );
}

const NAV = [
  ["Home", "top"], ["Our Company", "about"], ["Products & Capabilities", "products"],
  ["Manufacturing & Quality", "quality"], ["About Us", "about"],
];

const PILLARS = [
  { icon: Globe2, title: "Global Sourcing Network", desc: "A vetted network of manufacturers catering to Tier-2 & 3 clients across 6 continents for reliable, competitive supply." },
  { icon: Cog, title: "Precision Mechanical Engineering", desc: "Gears, gearboxes, sprockets, vehicle spares, mounts and components engineered to micron tolerances against your specs." },
  { icon: MapPin, title: "Local Presence", desc: "On-the-ground sales representation and technical support in the Greater Toronto Area." },
  { icon: ShieldCheck, title: "ISO Quality Verification", desc: "Every shipment validated against ISO 9001, DIN, AGMA, ASTM, AS, IATF & CSA standards with full documentation." },
  { icon: Truck, title: "End-to-End Logistics", desc: "Door-to-door freight, customs and last-mile delivery managed as one seamless flow." },
  { icon: Boxes, title: "Strategic Sourcing", desc: "Cost-effective offshore rates with high-volume production and prompt delivery." },
  { icon: Wrench, title: "Custom Manufacturing Specs", desc: "Build-to-print from your drawings — full customization. You specify it, we deliver it, including reverse engineering." },
  { icon: Landmark, title: "Enterprise Trust & Governance", desc: "Transparent, compliant and accountable partnerships built for the long term." },
];

const BADGES = ["ISO 9001", "AGMA", "ASTM", "AS9100", "IATF 16949", "CSA"];

const CLIENT_LOGOS = [
  ["Tata Steel", "https://www.ratnagears.com/images/logo/tata-steel.png", "Asia Pacific"],
  ["ArcelorMittal", "https://www.ratnagears.com/images/logo/arcelor-mittal.jpg", "Europe"],
  ["JSW Steel", "https://www.ratnagears.com/images/logo/jsw.jpg", "Asia Pacific"],
  ["Bharat Forge", "https://www.ratnagears.com/images/logo/bharat-forge.jpg", "Asia Pacific"],
  ["Kalyani Steels", "https://www.ratnagears.com/images/logo/kalyani-steels-ltd.png", "Asia Pacific"],
  ["Jindal Saw", "https://www.ratnagears.com/images/logo/jindal-saw-ltd.png", "Asia Pacific"],
  ["ISMT Limited", "https://www.ratnagears.com/images/logo/ismt-limited.png", "Asia Pacific"],
  ["John Deere", "https://www.ratnagears.com/images/logo/john-deere-pune.png", "Asia Pacific"],
  ["Mahindra Sanyo", "https://www.ratnagears.com/images/logo/mahindra%20sanyo.png", "Asia Pacific"],
  ["Ramkrishna Forgings", "https://www.ratnagears.com/images/logo/ramkrishna-forgings-limited-.png", "Asia Pacific"],
  ["Essar Steel", "https://www.ratnagears.com/images/logo/essar-steel.png", "Asia Pacific"],
  ["Sandvik", "https://www.ratnagears.com/images/logo/sandvik.jpg", "Europe"],
  ["Kirloskar", "https://www.ratnagears.com/images/logo/kirloskar.jpg", "Asia Pacific"],
  ["KONE", "https://www.ratnagears.com/images/logo/kone.jpg", "Europe"],
  ["VRDE Nagar", "https://www.ratnagears.com/images/logo/vrde-nagar.png", "Asia Pacific"],
  ["SULB", "https://www.ratnagears.com/images/logo/sulb.jpg", "Middle East"],
  ["Bebitz Flanges", "https://www.ratnagears.com/images/logo/bebitz-logo.png", "Europe"],
  ["KEMS Forgings", "https://www.ratnagears.com/images/logo/kems-forgings.png", "Asia Pacific"],
  ["LGB Forgings", "https://www.ratnagears.com/images/logo/lgb-forgings.png", "Asia Pacific"],
  ["Perfect Actuators", "https://www.ratnagears.com/images/logo/perfect-actuators.png", "Asia Pacific"],
  ["RSB Transmission", "https://www.ratnagears.com/images/logo/rsb.jpg", "US"],
  ["Canrig Drilling", "/clients/canrig.png", "US"],
  ["A&M Aerostructure", null, "US"],
  ["Spectra Inc.", null, "US"],
  ["ENEDYM Inc.", null, "North America"],
  ["Tembo Steels", "https://www.ratnagears.com/images/logo/tembo-steels.jpg", "Africa"],
  ["Sona Alloys", "https://www.ratnagears.com/images/logo/sona-alloys.jpg", "Asia Pacific"],
  ["Saar Loha", "https://www.ratnagears.com/images/logo/saar-loha.jpg", "Asia Pacific"],
  ["Hawkins", "https://www.ratnagears.com/images/logo/hawkins.jpg", "Asia Pacific"],
  ["Ahleia", "https://www.ratnagears.com/images/logo/ahleia.jpg", "Middle East"],
  ["Zamil Steel", "https://www.ratnagears.com/images/logo/zama.jpg", "Europe"],
  ["MSI", "https://www.ratnagears.com/images/logo/msi.jpg", "Africa"],
];

const REGION_CHIPS = ["North America", "US", "South America", "Europe", "Middle East", "Africa", "Asia Pacific", "East Asia"];

const FOOTPRINT = [
  ["6", "Continents served"], ["35+", "Years of manufacturing"], ["2m", "Max component diameter"], ["100%", "Quality-verified shipments"],
];

function Nav({ scrollTo }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <button onClick={() => scrollTo("top")} data-testid="logo-home"><Logo /></button>
        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map(([label, id]) => (
            <button key={id} data-testid={`nav-${id}`} onClick={() => scrollTo(id)}
              className="font-head font-semibold text-[13px] tracked-sm uppercase text-[#0A192F]/70 hover:text-[#0A192F] transition-colors duration-300">
              {label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-8">
          <button onClick={() => scrollTo("quote")} data-testid="nav-quote-btn"
            className="btn-gold hidden sm:inline-flex items-center gap-2 font-head font-bold text-[13px] tracked-sm uppercase px-6 py-3 rounded-lg">
            Get Precision Quote <ArrowUpRight size={16} />
          </button>
          <button className="lg:hidden text-[#0A192F]" onClick={() => setOpen(!open)} data-testid="mobile-toggle">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-6 py-5 flex flex-col gap-4">
          {NAV.map(([label, id]) => (
            <button key={id} onClick={() => { scrollTo(id); setOpen(false); }}
              className="text-left font-head font-semibold text-sm uppercase tracked-sm text-[#0A192F]/70">{label}</button>
          ))}
        </div>
      )}
    </header>
  );
}

function Hero({ scrollTo }) {
  return (
    <section id="top" className="relative bg-[#0A192F] text-white overflow-hidden">
      <div className="absolute inset-0 hero-lines" aria-hidden="true" />
      <div className="absolute inset-0 hero-glow" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-40 pb-28 lg:pt-48 lg:pb-36 text-center">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}
          className="inline-flex items-center gap-2 border border-[#D4AF37]/40 rounded-full px-4 py-1.5 mb-10">
          <span className="w-1.5 h-1.5 rounded-full gold-gradient" />
          <span className="font-head text-[11px] tracked uppercase text-[#D4AF37]">Global Supply Chain · Precision Engineering</span>
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.1 }}
          className="font-head font-extrabold tracked uppercase leading-[1.05] text-4xl sm:text-5xl lg:text-[56px] max-w-5xl mx-auto">
          <span className="silver-text">Driven By Precision.</span><br />
          <span className="silver-text">Powered By Trust </span><span className="gold-text">&amp; Global Partnerships.</span>
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.25 }}
          className="text-slate-300 text-lg leading-relaxed max-w-3xl mx-auto mt-8">
          Delivering high-performance custom <b className="text-white font-semibold">Gear Manufacturing up to 2 metres</b>, <b className="text-white font-semibold">Heavy Industrial Gearboxes</b>, <b className="text-white font-semibold">Oil &amp; Gas</b>, <b className="text-white font-semibold">Defense &amp; Strategic Engineering</b>, <b className="text-white font-semibold">Steel &amp; Sinter</b>, <b className="text-white font-semibold">Mining</b>, <b className="text-white font-semibold">Cement</b> &amp; <b className="text-white font-semibold">Agriculture Industry</b>, reverse engineering, heavy-industry spares and bespoke USP requirements.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.4 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
          <button onClick={() => scrollTo("quote")} data-testid="hero-primary-cta"
            className="btn-gold w-full sm:w-auto inline-flex items-center justify-center gap-2 font-head font-bold text-sm tracked-sm uppercase px-8 py-4 rounded-lg">
            Request Consultation <ArrowRight size={18} />
          </button>
          <button onClick={() => scrollTo("pillars")} data-testid="hero-secondary-cta"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-white/30 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white font-head font-bold text-sm tracked-sm uppercase px-8 py-4 rounded-lg transition-colors duration-300">
            Explore Services
          </button>
        </motion.div>
      </div>
    </section>
  );
}

function Footprint() {
  return (
    <section className="bg-white border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 divide-x divide-slate-100">
        {FOOTPRINT.map(([n, l], i) => (
          <Reveal key={i} delay={i * 0.08} className="py-10 lg:py-12 px-4 text-center">
            <div className="font-head font-extrabold text-4xl lg:text-5xl gold-text">{n}</div>
            <div className="font-head text-[11px] tracked uppercase text-[#0A192F]/50 mt-3">{l}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Landing() {
  const location = useLocation();
  const [region, setRegion] = useState(null);
  const scrollTo = (id) => {
    if (id === "top") return window.scrollTo({ top: 0, behavior: "smooth" });
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: "smooth" });
  };

  useEffect(() => {
    const target = location.state?.scrollTo;
    if (target) {
      setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth" }), 250);
    }
  }, [location.state]);

  return (
    <div className="bg-white">
      <SiteNav />
      <Hero scrollTo={scrollTo} />
      <Footprint />

      {/* Pillars */}
      <section id="pillars" className="bg-[#F8FAFC] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal className="text-center max-w-2xl mx-auto mb-16">
            <span className="font-head font-bold tracked uppercase text-xl lg:text-2xl gold-text">CORE PILLARS</span>
            <h2 className="font-head font-extrabold tracked uppercase text-3xl lg:text-4xl text-[#0A192F] mt-4 leading-tight">
              Eight Disciplines.<br />One Standard.
            </h2>
            <p className="text-slate-500 mt-5 leading-relaxed">
              A fully integrated capability stack — from raw material to delivered precision component.
            </p>
          </Reveal>
          <div id="sourcing" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((p, i) => (
              <Reveal key={i} delay={(i % 4) * 0.07}>
                <div data-testid={`pillar-${i}`} className="card-lift group bg-white rounded-lg shadow-card border border-slate-100 p-7 h-full">
                  <div className="w-12 h-12 rounded-lg gold-gradient flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <p.icon size={22} color={NAVY} strokeWidth={2} />
                  </div>
                  <h3 className="font-head font-bold text-[#0A192F] text-base leading-snug uppercase tracked-sm">{p.title}</h3>
                  <p className="text-slate-500 text-sm mt-3 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* About / value band */}
      <section id="about" className="bg-[#0A192F] text-white py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 hero-lines" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <span className="font-head font-bold tracked uppercase text-xl lg:text-2xl gold-text">About Exon Global</span>
            <h2 className="font-head font-extrabold tracked uppercase text-3xl lg:text-4xl mt-4 leading-tight">
              Institutional Reliability,<br /><span className="gold-text">Engineered Worldwide.</span>
            </h2>
            <p className="text-slate-300 mt-6 leading-relaxed">
              Exon Global is the primary regional partner headquartered in the Greater Toronto Area — the technical interface and sales/distribution arm for Ratna Gears Pvt. Ltd. and other manufacturers. We source, verify and deliver mission-critical components for oil &amp; gas, defence, agriculture, mining, sugar industry and heavy industry — steel &amp; sinter plants — anchored in trust, governed with transparency.
            </p>
            <p className="text-slate-400 mt-4 leading-relaxed text-sm">
              <span className="gold-text font-semibold">35+ years manufacturing heritage.</span> Cost-effective, high-volume production with precision tolerances, backed by decades of supply to rigorous US, Middle Eastern and African markets.
            </p>
            <div className="mt-8 space-y-3">
              {["ISO 9001 certified manufacturing partners", "Direct factory pricing, no hidden layers", "Single accountable point of contact"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-slate-200">
                  <CheckCircle2 size={18} className="text-[#D4AF37] shrink-0" /> {t}
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="grid grid-cols-2 gap-5">
              {[["Provenance", "35+ years at the machine"], ["Precision", "1–16 module · Ø up to 2m"], ["Reach", "Six continents sourced"], ["Governance", "Fully compliant supply"]].map(([k, v], i) => (
                <div key={i} className="border border-white/10 rounded-lg p-6 bg-white/[0.03]">
                  <div className="font-head text-[11px] tracked uppercase text-[#D4AF37]">{k}</div>
                  <div className="font-head font-bold text-lg mt-2">{v}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission Statement */}
      <section id="mission" className="bg-white py-24 lg:py-32 border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <span className="font-head font-bold tracked uppercase text-xl lg:text-2xl gold-text">Our Mission</span>
          <p className="font-head font-bold text-[#0A192F] text-2xl sm:text-3xl lg:text-[38px] leading-[1.25] tracking-tight mt-8">
            Exon Global is committed to connecting world-class engineering solutions with industry through
            <span className="gold-text"> precision, trust, and enduring global partnerships.</span> We strive to
            create sustainable value for manufacturers and customers by delivering reliable products, responsive
            support, and long-term business relationships.
          </p>
        </div>
      </section>

      {/* Products & Capabilities */}
      <ProductsCapabilities />

      {/* Manufacturing & Quality */}
      <ManufacturingQuality />

      {/* Quote calculator */}
      <QuoteSection />

      {/* Trust badges */}
      <section id="trust" className="bg-white py-20 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <span className="font-head font-bold tracked uppercase text-xl lg:text-2xl gold-text">Global Footprint & Clients</span>
            <h2 className="font-head font-extrabold tracked uppercase text-2xl lg:text-3xl text-[#0A192F] mt-4">
              Trusted Across Continents.
            </h2>
            <div className="flex flex-wrap items-center justify-center gap-2.5 mt-6">
              {REGION_CHIPS.map((r) => (
                <button key={r} data-testid={`region-chip-${r}`} onClick={() => setRegion(region === r ? null : r)}
                  className={`inline-flex items-center gap-2 border rounded-full px-4 py-1.5 text-xs font-head tracked-sm uppercase transition-colors ${region === r ? "border-[#D4AF37] text-[#0A192F] bg-[#FFF8E1]" : "border-slate-200 text-[#708090] hover:border-[#D4AF37]"}`}>
                  <span className="w-1.5 h-1.5 rounded-full gold-gradient" />{r}
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mt-12">
              <WorldMap onRegionSelect={(r) => setRegion(region === r ? null : r)} activeRegion={region} />
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            {region && (
              <div className="flex items-center justify-center gap-3 mt-12 mb-2" data-testid="region-filter-bar">
                <span className="font-head text-sm tracked-sm uppercase text-[#0A192F]">Showing clients · <span className="gold-text font-bold">{region}</span></span>
                <button onClick={() => setRegion(null)} data-testid="clear-region" className="text-xs font-head tracked-sm uppercase text-[#708090] hover:text-[#D4AF37] border border-slate-200 rounded-full px-3 py-1">Show all ✕</button>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-6">
              {(region ? CLIENT_LOGOS.filter((c) => c[2] === region) : CLIENT_LOGOS).map(([name, src]) => (
                <div key={name} data-testid={`client-${name}`}
                  className="flex items-center justify-center h-24 bg-white border border-slate-100 rounded-lg p-5 shadow-card">
                  {src
                    ? <img src={src} alt={name} loading="lazy" title={name}
                        className="max-h-12 max-w-full object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
                    : <span className="font-head font-bold text-sm tracked-sm uppercase text-slate-400 hover:text-[#0A192F] text-center leading-snug transition-colors duration-300">{name}</span>}
                </div>
              ))}
            </div>
            {region && CLIENT_LOGOS.filter((c) => c[2] === region).length === 0 && (
              <p className="text-slate-500 mt-8 text-center" data-testid="region-empty">Exon Global maintains an on-the-ground presence here — clients in this region are served directly from our global manufacturing network.</p>
            )}
          </Reveal>

          <Reveal>
            <div className="font-head font-bold tracked uppercase text-xl lg:text-2xl gold-text mt-20 mb-2">Compliance & Standards</div>
            <div className="flex flex-wrap items-center justify-center gap-4 lg:gap-6 mt-4">
              {BADGES.map((b) => (
                <div key={b} data-testid={`badge-${b}`}
                  className="flex items-center gap-2.5 border border-slate-200 rounded-lg px-6 py-4 text-[#708090] hover:text-[#0A192F] hover:border-[#D4AF37] transition-colors duration-300">
                  <Award size={20} />
                  <span className="font-head font-bold text-sm tracked-sm uppercase">{b}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function Footer({ scrollTo }) {
  return (
    <footer className="bg-[#0A192F] text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Logo light />
          <p className="text-slate-400 mt-6 max-w-sm leading-relaxed">
            Global sourced precision, built on trust. Your enterprise partner for supply chain, precision engineering and quality-verified component sourcing.
          </p>
        </div>
        <div className="md:col-span-3">
          <div className="font-head text-[11px] tracked uppercase text-[#D4AF37] mb-4">Global Headquarters</div>
          <div className="flex items-start gap-2 text-slate-300 text-sm leading-relaxed">
            <MapPin size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
            <span>Attn: Ken Shaw<br />Suite 329, 17 Worthington Ave<br />Brampton, ON L7A 2Y7, Canada</span>
          </div>
          <a href="tel:+12899466435" data-testid="footer-phone" className="block mt-4 text-white hover:text-[#D4AF37] transition-colors">289.946.6435</a>
          <a href="mailto:info@exonglobal.ca" data-testid="footer-email" className="inline-block mt-1 text-white hover:text-[#D4AF37] transition-colors">info@exonglobal.ca</a>
        </div>
        <div className="md:col-span-4">
          <div className="font-head text-[11px] tracked uppercase text-[#D4AF37] mb-4">Navigate</div>
          <div className="grid grid-cols-2 gap-2">
            {NAV.concat([["Get a Quote", "quote"]]).map(([l, id]) => (
              <button key={id} onClick={() => scrollTo(id)} className="text-left text-slate-300 hover:text-white transition-colors text-sm">{l}</button>
            ))}
          </div>
          <Link to="/admin/login" data-testid="admin-link" className="inline-block mt-6 text-slate-500 hover:text-[#D4AF37] transition-colors text-sm">Admin login →</Link>
        </div>
      </div>
      <div className="border-t border-white/10 py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-slate-500 text-[11px] leading-relaxed mb-4 max-w-4xl">
            Exon Global operates as the authorised regional promotion, technical interface and distribution partner for Ratna Gears Pvt. Ltd. and other manufacturers. All trademarks, brand names and client logos are the property of their respective owners and are shown for reference only.
          </p>
          <div className="flex flex-wrap justify-between gap-4 text-slate-500 text-xs">
            <span>© {new Date().getFullYear()} Exon Global. All rights reserved.</span>
            <div className="flex gap-6">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors">Legal Disclosures</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
